import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { TreeDataModule } from 'ag-grid-enterprise';
import { OmniDropdownComponent, OmniIconComponent, Utils, OverBudgetWarningModal } from '../../../../../shared';
import { PlacementDetailsTableService } from "../../../../placements";
import SyncWithPrismaConfirmation from './SyncWithPrismaConfirmation';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule, TreeDataModule]);

/**
 * CreateMediaOrderWizard - A multi-step wizard component for creating media orders
 * 
 * This component provides a 3-step wizard interface:
 * Step 1: Taxonomy-compliant order naming
 * Step 2: Channel and publisher budget selection with inline editing
 * Step 3: Order details confirmation
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls wizard visibility
 * @param {Function} props.onClose - Callback when wizard is closed
 * @param {Function} props.onSubmit - Callback when order creation is submitted
 * @param {Object} props.campaignTaxonomyData - Campaign taxonomy configuration and fields
 * @param {Array} props.campaignData - Campaign data including dates, budget, and channels
 * @param {Object} props.systemParametersData - System parameters including currency settings
 * @param {boolean} props.editMediaOrderChannels - Whether editing existing media order channels
 * @param {Object} props.parentCampaignObj - Parent campaign object for edit mode
 */

const CreateMediaOrderWizard = ({ isOpen, onClose, onSubmit, campaignTaxonomyData, campaignData, systemParametersData, editMediaOrderChannels, parentCampaignObj }) => {

    const [currentStep, setCurrentStep] = useState(1);
    const gridRef = useRef(null);
    const [taxonomyPreview, setTaxonomyPreview] = useState('');
    const [selectedTotal, setSelectedTotal] = useState(0);
    const [selectedChannelData, setSelectedChannelData] = useState([]);
    const [showPrismaConfirmation, setShowPrismaConfirmation] = useState(false);
    const [ emptyFieldError, setEmptyFieldError] = useState(false);
    const payloadData = useRef({
        campaign_taxonomy_display: '',
        taxonomy_schema: '',
        taxonomy_client_id: campaignTaxonomyData?.clientid,
        taxonomy_schema_id: campaignTaxonomyData?.id,
        taxonomy: []
    });

    const localCurrency = campaignData[0]?.local_currency_name;
    const isLocal = systemParametersData?.data?.isLocal;

    /**
     * Extracts and parses parent campaign's taxonomy data
     * 
     * Retrieves the campaign's taxonomy information and splits it into a structured object
     * using the taxonomy schema. This data is used to pre-populate form fields.
     * 
     * @returns {Object} Parsed taxonomy data object with field values
     */
    const getParentTaxonomyData = () => {
        let campaign = campaignData[0];
        let taxonomyJson = campaign?.taxonomy?.length ? campaign?.taxonomy : null;
        let campaignTaxonomy = campaign?.campaign_taxonomy?.split(':|');
        let taxonomyName = campaignTaxonomy?.[0];
        let taxonomySchema = campaign?.taxonomy_schema ? campaign?.taxonomy_schema : null;
        let taxonomyData = campaignTaxonomyData?.fields;

        return PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomyData, '', null, taxonomyJson);
    };

    /**
     * Initializes form data with default values from campaign data
     * 
     * Creates initial form state including:
     * - Flight dates from campaign
     * - Client code
     * - Dynamic taxonomy fields with values from parent campaign or "NU" (Not Used)
     * 
     * @returns {Object} Initial form data object with all required fields
     */
    const initializeFormData = () => {
        const initialData = {
            flight: {
                startDate: campaignData[0]?.start_date || '',
                endDate: campaignData[0]?.end_date || ''
            },
            planned: '',
            ordered: '',
            costType: '',
            clientCode: campaignData[0]?.client_code,
            orderBudget: ''
        };
        let taxonomyData = getParentTaxonomyData();

        // Add dynamic fields from campaignTaxonomyData
        if (campaignTaxonomyData?.fields) {
            let taxonomySchema = '';
            campaignTaxonomyData.fields.forEach(field => {
                initialData[field.alias || field.name] = taxonomyData[field.alias || field.name] || "NU";
                if (!field.hidden) {
                    taxonomySchema += field.name + '|';
                }
            });
            payloadData.current.taxonomy_schema = taxonomySchema.slice(0, -1);
        }

        return initialData;
    };

    const [formData, setFormData] = useState(() => initializeFormData());

    /**
     * Transforms campaign channel data into AG Grid tree structure
     * 
     * Creates a hierarchical data structure with:
     * - Parent nodes: Channels with aggregated budgets and date ranges
     * - Child nodes: Publishers with individual budgets and dates
     * 
     * Handles currency conversion based on local/USD settings and formats
     * dates to MM/DD/YY format for display.
     * 
     * @returns {Array} Array of channel objects with nested publisher data
     */
    const gridChannelData = useMemo(() => {
        const channels = editMediaOrderChannels ? parentCampaignObj?.media_attributes?.channels : campaignData[0]?.media_attributes?.channels;
        if (!channels || !Array.isArray(channels)) return [];

        return channels.map((channel, channelIndex) => {
            // Format date range for channel
            const channelStartDate = channel.channel_startdate ? new Date(channel.channel_startdate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelEndDate = channel.channel_enddate ? new Date(channel.channel_enddate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
            const channelDateRange = channelStartDate && channelEndDate ? `${channelStartDate} - ${channelEndDate}` : '';

            // Transform publishers (nestedData)
            const publishers = channel.publishers?.map((publisher, pubIndex) => {
                const pubStartDate = publisher.publisher_start_date ? new Date(publisher.publisher_start_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubEndDate = publisher.publisher_end_date ? new Date(publisher.publisher_end_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '';
                const pubDateRange = pubStartDate && pubEndDate ? `${pubStartDate} - ${pubEndDate}` : publisher.flight || '';
                let publisherIsInParentCampaign = false;

                if (editMediaOrderChannels) { // Check if publisher exists in parent campaign data and check it
                    const matchingChannel = campaignData[0]?.media_attributes?.channels?.find(ch => ch.channel_display_name === channel.channel_display_name || ch.channel_name === channel.channel_name) || {};
                    if (Object?.keys(matchingChannel).length > 0) {
                        const selectedPublisher = matchingChannel.publishers?.find(pub => pub.publisher_display_name === publisher.publisher_display_name || pub.publisher_name === publisher.publisher_name) || {};
                        if (Object?.keys(selectedPublisher).length > 0) {
                            publisherIsInParentCampaign = true;
                        }
                    }
                }

                let placementsAvailableInChild = false;
                if (editMediaOrderChannels) {
                    const childCampaignChannels = campaignData[0]?.media_attributes?.channels || [];
                    const childChannel = childCampaignChannels.find(ch => ch.channel_display_name === channel.channel_display_name || ch.channel_name === channel.channel_name);
                    const childPublishers = childChannel?.publishers || [];
                    const childPublisher = childPublishers.find(pub => pub.publisher_display_name === publisher.publisher_display_name || pub.publisher_name === publisher.publisher_name);
                    placementsAvailableInChild = childPublisher ? childPublisher.publisher_budget_by_placements > 0 : false;
                }

                return {
                    id: `publisher-${channelIndex}-${pubIndex}`,
                    channel: publisher.publisher_display_name || publisher.publisher_name,
                    date: pubDateRange,
                    availablePlannedSpend: localCurrency && isLocal ? publisher.publisher_budget_local : publisher.publisher_budget || 0,
                    isChannel: false,
                    publisherIsInParentCampaign: publisherIsInParentCampaign,
                    placementsAreAvailable: editMediaOrderChannels ? placementsAvailableInChild : false, // todo: replace with flag from backend
                    // Keep original data for reference
                    originalData: publisher
                };
            }) || [];

            return {
                id: `channel-${channelIndex}`,
                channel: channel.channel_display_name || channel.channel_name,
                date: channelDateRange,
                availablePlannedSpend: localCurrency && isLocal ? channel.channel_total_budget_in_pmds_local : channel.channel_total_budget_in_pmds || 0,
                isChannel: true,
                nestedData: publishers,
                // Keep original data for reference
                originalData: channel
            };
        });
    }, [
        editMediaOrderChannels,
        parentCampaignObj?.media_attributes?.channels,
        campaignData,
        localCurrency,
        isLocal
    ]);

    const totalSteps = 2;

    /**
     * Handles input field changes in Step 1 (taxonomy fields)
     * 
     * Updates form data, extracting the ID from dropdown selections
     * or using the value directly for text inputs.
     * 
     * @param {string} field - Field name to update
     * @param {string|Object} value - New value or dropdown selection object with id property
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value?.id ? value.id : value,
            [field + "_displayName"]: value?.value ? value.value : value,
        }));
    };

    /**
     * Handles AG Grid selection changes for channel/publisher selection
     * 
     * Groups selected publishers by their parent channel and calculates total budget.
     * Only publisher nodes (children) are considered - parent channel selections are filtered out.
     * Also prevents deselection of locked rows (publisherIsInParentCampaign with low budget).
     * 
     * Structure of grouped data:
     * [
     *   {
     *     channel: { id: "channel-1", channel: "Display", ... },
     *     publishers: [
     *       { id: "publisher-1-1", channel: "Samsung", parent: {...}, ... },
     *       { id: "publisher-1-2", channel: "Google", parent: {...}, ... }
     *     ]
     *   }
     * ]
     * 
     * @param {Object} event - AG Grid selection event
     * @param {Object} event.api - AG Grid API instance
     */
    const handleSelectionChange = useCallback((event) => {
        const api = event.api;

        // Re-select any locked rows that were deselected (only in edit mode)
        if (editMediaOrderChannels) {
            api.forEachNode((node) => {
                const data = node.data;
                if (data && !data.isChannel && data.publisherIsInParentCampaign && data.placementsAreAvailable) {
                    if (!node.isSelected()) {
                        // Use setTimeout to avoid modifying selection during selection change event
                        setTimeout(() => {
                            node.setSelected(true);
                        }, 0);
                    }
                }
            });
        }

        const selectedNodes = api.getSelectedNodes();

        // Filter only publisher nodes (children)
        const publishers = selectedNodes
            .filter(node => !node.data.isChannel)
            .map(node => ({
                ...node.data,
                parent: node.parent ? node.parent.data : null
            }));

        // Group publishers by parent channel
        const channelMap = new Map();
        publishers.forEach(publisher => {
            const parentId = publisher.parent?.id;
            if (parentId) {
                if (!channelMap.has(parentId)) {
                    channelMap.set(parentId, {
                        channel: publisher.parent,
                        publishers: []
                    });
                }
                channelMap.get(parentId).publishers.push(publisher);
            }
        });

        // Convert Map to Array
        const groupedData = Array.from(channelMap.values());

        // Store grouped data for API submission
        setSelectedChannelData(groupedData);

        // Calculate total from selected publishers only
        const total = publishers.reduce((sum, item) => {
            return sum + (item.availablePlannedSpend || 0);
        }, 0);
        setSelectedTotal(total);
    }, [editMediaOrderChannels]);

    /**
     * Validates if all required fields are filled for the current step
     * Step 1: At least one publisher must be selected
     * Step 2: All mandatory taxonomy fields must have values
     * @returns {boolean} True if all required fields are filled, false otherwise
     */
    const isFormValid = () => {
        if (currentStep === 1) {
            // Check if at least one publisher is selected
            return selectedChannelData.some(item => !item.isChannel);
        } 
        // else if (currentStep === 2) {
        //     // Check if all mandatory fields are filled
        //     return campaignTaxonomyData?.fields?.every(field => {
        //         if (field.mandatory || field.is_required) {
        //             const value = formData[field.alias || field.name];
        //             return value && value !== '' && value !== 'NU';
        //         }
        //         return true;
        //     }) ?? true;
        // }
        return true;
    };

    /**
     * Handles navigation to next step
     * Proceeds to the next step if not on the last step.
     */
    const handleNext = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
        }
    };

    /**
     * Handles navigation to previous step
     * Decrements the current step if not on the first step.
     */
    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    /**
     * Handles final form submission by showing Prisma sync confirmation
     * Opens the confirmation dialog instead of directly submitting.
     */
    const handleSubmit = () => {
        let isValid = campaignTaxonomyData?.fields?.every(field => {
            if (field.mandatory || field.is_required) {
                const value = formData[field.alias || field.name];
                if (!value || value === '' || value === 'NU') {
                    return false;
                }
            }
            return true;
        })
        if(!isValid){
            // If form is not valid, navigate back to Step 2 to show validation errors
            setEmptyFieldError(true); // Reset error state to trigger re-render
            const errorFields = document.querySelector('.error-label');
            if (errorFields) {
                errorFields.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }else{
            setEmptyFieldError(false); 
            setShowPrismaConfirmation(true);
        }
    };

    /**
     * Handles cancellation of Prisma sync confirmation
     * Closes the confirmation dialog without any action.
     */
    const handlePrismaConfirmationCancel = () => {
        setShowPrismaConfirmation(false);
    };

    /**
     * Handles Prisma sync confirmation submission
     * Closes the confirmation dialog and calls onSubmit with the specified sync preference
     * @param {boolean} syncWithPrisma - Whether to sync with Prisma or not
     */
    const handlePrismaSync = (syncWithPrisma) => {
        setShowPrismaConfirmation(false);
        onSubmit({ ...formData, syncWithPrisma, selectedChannels: selectedChannelData, campaignTaxonomy: taxonomyPreview, ...payloadData.current });
    };

    /**
     * AG Grid ready callback - restores previously selected rows
     * When the grid initializes or re-renders, this callback restores
     * any previously selected channels/publishers to maintain selection
     * state across renders. Also auto-selects publishers that exist in
     * the parent campaign (publisherIsInParentCampaign === true).
     * @param {Object} params - AG Grid params object with api reference
     */
    const onGridReady = React.useCallback((params) => {
        // Auto-select publishers that exist in parent campaign
        params.api.forEachNode((node) => {
            if (node.data?.publisherIsInParentCampaign) {
                node.setSelected(true);
            }
        });

        // Restore previously selected rows if any
        if (selectedChannelData.length > 0) {
            const selectedIds = selectedChannelData.map(item => item.id);
            params.api.forEachNode((node) => {
                if (selectedIds.includes(node.data?.id)) {
                    node.setSelected(true);
                }
            });
        }
    }, [selectedChannelData]);

    /**
     * Generates taxonomy-compliant order name preview
     * Concatenates form field values with their configured delimiters
     * according to the taxonomy schema. Hidden fields are excluded.
     * The last field doesn't include a delimiter.
     * @returns {string} Formatted taxonomy preview string
     */
    const generateTaxonomyPreview = () => {
        const taxonomy = [];
        const parts = campaignTaxonomyData?.fields?.map((field, index) => {
            const value = formData[field.alias || field.name] || (field.field_type === 10 ? '{TaxonomyValueID}' : 'NU');
            const displayName = formData[(field.alias || field.name) + "_displayName"] || value;
            taxonomy.push({
                id: field.id,
                hidden: field.hidden,
                code: value,
                delimiter: field.delimiter_value || ''
            });

            if (!field.hidden) {
                if (index === campaignTaxonomyData.fields.length - 1) {
                    return { taxonomyValue: value, displayName: displayName };
                }
                return { taxonomyValue: value + (field.delimiter_value || ''), displayName: displayName + (field.delimiter_value || '') };
            }
            return '';
        });

        setTaxonomyPreview(parts?.map(part => part.taxonomyValue).join(''));
        payloadData.current.taxonomy = taxonomy;
        payloadData.current.campaign_taxonomy_display = parts?.map(part => part.displayName).join('');
    };

    // Update preview whenever formData changes
    React.useEffect(() => {
        generateTaxonomyPreview();
    }, [formData]);

    /**
     * Custom cell renderer for the Available Planned Spend column
     * 
     * Displays formatted currency value based on campaign settings (local or USD).
     * Zero values are shown in a grayed-out style.
     * 
     * @param {Object} props - AG Grid cell renderer props
     * @param {Object} props.data - Row data object
     * @param {number} props.value - Cell value (budget amount)
     * @returns {JSX.Element} Rendered cell content
     */
    const AvailablePlannedSpendRenderer = (props) => {
        const currency = localCurrency && isLocal ? localCurrency : 'USD';

        const formattedValue = props.value?.toLocaleString('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) || '$0.00';

        const isZeroValue = props.value === 0;

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                width: '100%'
            }}>
                <span style={{
                    textAlign: 'right',
                    color: isZeroValue ? '#d3d3d3' : 'inherit'
                }}>
                    {formattedValue}
                </span>
            </div>
        );
    };

    // Column definitions for AG Grid (defined outside render to avoid recreation)
    const columnDefs = React.useMemo(() => [
        {
            field: 'channel',
            headerName: 'CHANNEL',
            cellRenderer: 'agGroupCellRenderer',
            showRowGroup: true,
            cellRendererParams: {
                suppressCount: true,
            },
            flex: 2
        },
        {
            field: 'date',
            headerName: 'DATE',
            flex: 1.5,
            cellStyle: (params) => {
                if (params.data?.isChannel && (!params.data?.date || params.data?.availablePlannedSpend === 0)) {
                    return { color: '#d3d3d3' };
                }
                return null;
            }
        },
        {
            field: 'availablePlannedSpend',
            headerName: 'AVAILABLE PLANNED SPEND',
            flex: 1.5,
            cellRenderer: AvailablePlannedSpendRenderer,

        }
    ], [localCurrency, isLocal]);

    const defaultColDef = React.useMemo(() => ({
        sortable: false,
        resizable: true,
        filter: false,
        suppressHeaderMenuButton: true
    }), []);

    const channelTableTheme = React.useMemo(() => themeQuartz.withParams({
        headerRowBorder: false,
        rowBorder: false,
        spacing: 8,
        wrapperBorder: false,
    }), []);

    /**
     * Determines if a row can be selected based on placementsAreAvailable
     * 
     * Only applies when editMediaOrderChannels is true:
     * For publisher rows (nested data): 
     *   - If publisherIsInParentCampaign is true: allow selection (will be pre-selected and locked)
     *   - Otherwise: disabled if placementsAreAvailable is true
     * For channel rows (parent): disabled if all nested publishers are disabled
     * 
     * @param {Object} params - AG Grid row node params
     * @returns {boolean} True if row can be selected, false otherwise
     */
    const isRowSelectable = React.useCallback((params) => {
        const data = params.data;
        if (!data) return false;

        // If not in edit mode, all rows are selectable
        if (!editMediaOrderChannels) return true;

        // For publisher rows (nested data)
        if (!data.isChannel) {
            // If publisher is in parent campaign, always allow selection
            // (will be pre-selected and locked in handleSelectionChange)
            if (data.publisherIsInParentCampaign) {
                return true;
            }
            // Otherwise, only allow if placementsAreAvailable is false
            return !data.placementsAreAvailable;
        }

        // For channel rows (parent), check if at least one publisher has placementsAreAvailable === false
        // Channel is disabled if ALL publishers have placementsAreAvailable === true
        if (data.nestedData && data.nestedData.length > 0) {
            return data.nestedData.some(publisher => !publisher.placementsAreAvailable);
        }

        // If no nested data, allow selection
        return true;
    }, [editMediaOrderChannels]);

    /**
     * Returns row class based on locked state
     * Locked rows get a special class:
     * - Publisher rows: publisherIsInParentCampaign with placementsAreAvailable=true
     * - Channel rows: all nested publishers are disabled (locked or placementsAreAvailable=true)
     * Only applies when editMediaOrderChannels is true
     * 
     * @param {Object} params - AG Grid row params
     * @returns {string} CSS class name
     */
    const getRowClass = React.useCallback((params) => {
        if (!editMediaOrderChannels) return '';
        
        const data = params.data;
        if (!data) return '';

        // For publisher rows
        if (!data.isChannel && data.publisherIsInParentCampaign && data.placementsAreAvailable) {
            return 'locked-row';
        }

        // For channel rows (parent), check if all publishers have placementsAreAvailable === true
        if (data.isChannel && data.nestedData && data.nestedData.length > 0) {
            const allPublishersDisabled = data.nestedData.every(publisher => 
                publisher.placementsAreAvailable
            );
            if (allPublishersDisabled) {
                return 'locked-row';
            }
        }

        return '';
    }, [editMediaOrderChannels]);

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="wizard-step" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <h2>Select the channels you're building placements for in this media order.</h2>

                        <div style={{ flex: 1, width: '100%', marginTop: '24px', minHeight: 0 }}>
                            <AgGridReact
                                ref={gridRef}
                                theme={channelTableTheme}
                                columnDefs={columnDefs}
                                rowData={gridChannelData}
                                defaultColDef={defaultColDef}
                                treeData={true}
                                treeDataChildrenField="nestedData"
                                getRowId={(params) => params.data.id}
                                rowSelection={{
                                    mode: "multiRow",
                                    groupSelects: "descendants",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: false,
                                    isRowSelectable: isRowSelectable,
                                }}
                                getRowClass={getRowClass}
                                onGridReady={onGridReady}
                                onSelectionChanged={handleSelectionChange}
                                groupDefaultExpanded={-1}
                                groupDisplayType={"custom"}
                                headerHeight={40}
                                rowHeight={40}
                            />
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="wizard-step">
                        <h2>Fill in the fields below to create a taxonomy compliant name for this order. You can update this later if needed.</h2>

                        {/* Taxonomy Preview */}
                        {taxonomyPreview && (
                            <div className="taxonomy-preview">
                                <div className="preview-label">Preview:</div>
                                <div className="preview-text">
                                    <i className="omni-icon-info"></i>
                                    {taxonomyPreview}
                                </div>
                            </div>
                        )}

                        {/* Dynamic Fields from campaignTaxonomyData - Grid Layout */}
                        <div className="form-fields-grid">
                            {campaignTaxonomyData?.fields?.map((field, index) => (
                                <div className="form-group" key={field.field_name || index}>
                                    {field.field_type === 1 ? (
                                        // Dropdown field
                                        (() => {
                                            const options = field.fielditems?.map(item => ({
                                                value: item.name,
                                                id: item.code
                                            })) || [];
                                            const currentValue = formData[field.alias || field.name];
                                            let isValidValue = '';
                                            if (currentValue) {
                                                isValidValue = options.filter(opt => opt.id?.toLowerCase() === currentValue?.toLowerCase());
                                            }
                                            formData[(field.alias || field.name) + "_displayName"] = isValidValue?.length ? isValidValue[0].value : 'NU';
                                            const showError = field.mandatory && emptyFieldError && (currentValue === '' || currentValue === 'NU');
                                            return (
                                                <div>
                                                    <label className={showError ? 'error-label' : ''}>
                                                       {field.mandatory ? '*' : ''}{field.alias || field.name}
                                                    </label>
                                                   <OmniDropdownComponent
                                                       typeahead
                                                       searchindropdown
                                                       hidefooter
                                                       placeholder="Please select"
                                                       onDropdownChange={(ev) => handleInputChange(field.alias || field.name, ev.detail)}
                                                       value={isValidValue?.length ? [currentValue] : []}
                                                       options={options}
                                                       className={showError ? 'error-field' : ''}
                                                   />
                                                   {showError && <span className="error-helper-text">{`${field.alias?field.alias:field.name} is required`}</span>}
                                                </div>
                                            );
                                        })()
                                    ) : field.field_type === 10 ? (
                                        // Disabled field with value or default "{TaxonomyValueID}"
                                        <div>
                                            <label>
                                                {field.mandatory ? '*' : ''}{field.alias || field.name}
                                            </label>
                                            <input
                                                type="text"
                                                value={formData[field.alias || field.name] || '{TaxonomyValueID}'}
                                                disabled
                                                placeholder={field.alias || field.name}
                                            />
                                        </div>
                                    ) : (
                                        (() => {
                                            const currentValue = formData[field.alias || field.name];
                                            const showError = field.mandatory && emptyFieldError && (currentValue === '' || currentValue === 'NU');
                                            return (
                                            <div>
                                                <label className={showError ? 'error-label' : ''}>
                                                    {field.mandatory ? '*' : ''}{field.alias || field.name}
                                                </label>
                                            {/* // Text input field */}
                                                <input
                                                    type="text"
                                                    value={formData[field.alias || field.name] || ''}
                                                    onChange={(e) => handleInputChange(field.alias || field.name, e.target.value)}
                                                    placeholder={field.alias || field.name}
                                                    required={field.is_required}
                                                    maxLength={field.character_limit || undefined}
                                                    className={showError ? 'error-field' : ''}
                                                />
                                                {showError && <span className="error-helper-text">{currentValue.toLowerCase() === 'nu' ?  `Text should not contains NU`: `${field.alias?field.alias:field.name} is required`}</span>}
                                            </div>
                                            )
                                        })()
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="create-media-order-wizard-overlay">
            <div className="create-media-order-wizard">
                <div className="wizard-header">
                    <h2>
                        {editMediaOrderChannels ? 'Edit Planned Channel' : 'Set Up New Order'}
                    </h2>
                    <div className="wizard-header-right">
                        {!editMediaOrderChannels && <span className="step-indicator">{currentStep} of {totalSteps}</span>}
                        <button className="tertiary" onClick={onClose}>
                            <OmniIconComponent icon-id="omni:interactive:close" />
                        </button>
                    </div>
                </div>

                <div className="wizard-content">
                    {renderStepContent()}
                </div>

                {currentStep === 1 && (
                    <div className="wizard-total-section">
                        <span className="total-label">Total:</span>
                        <span className="total-amount">
                            {selectedTotal.toLocaleString('en-US', {
                                style: 'currency',
                                currency: localCurrency && isLocal ? localCurrency : 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                        {/* <OmniIconComponent 
                            icon-id="omni:informative:info" 
                            style={{ marginLeft: '8px', fontSize: '16px', cursor: 'pointer', color: '#999' }}
                        /> */}
                    </div>
                )}

                <div className="wizard-footer">
                    {currentStep === 2 && (
                        <div className="wizard-actions-left">
                            <span className="required-note">* Required fields</span>
                        </div>
                    )}
                    <div className="wizard-actions-right">
                        <button className="tertiary" onClick={onClose}>
                            Cancel
                        </button>
                        {editMediaOrderChannels ? (
                            <button onClick={() => onSubmit(selectedChannelData, campaignData[0]?.synthetic_id)} disabled={!isFormValid()}>Done</button>
                        ) : (
                            <>
                                {currentStep > 1 && (<button className="secondary" onClick={handlePrevious}>Back</button>)}
                                {currentStep < totalSteps ? (
                                    <button onClick={handleNext} disabled={!isFormValid()}>Next</button>
                                ) : (
                                    <button onClick={handleSubmit} disabled={!isFormValid()}>Create Media Order</button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Prisma Sync Confirmation Modal */}
            <SyncWithPrismaConfirmation
                isOpen={showPrismaConfirmation}
                onCancel={handlePrismaConfirmationCancel}
                onDontSync={() => handlePrismaSync(false)}
                onSync={() => handlePrismaSync(true)}
            />
        </div>
    );
};

export default CreateMediaOrderWizard;
